'use client';

import React from 'react';

export interface CartItem {
  id: string;
  name: string;
  description?: string;
  image_url?: string;
  price: number;
  quantity: number;
}

interface State {
  items: CartItem[];
  totalItems: number;
  totalUniqueItems: number;
  isEmpty: boolean;
}

interface CartProviderState extends State {
  addItemToCart: (_item: any) => void;
  removeItemFromCart: (_id: string) => void;
  clearItemFromCart: (_id: string) => void;
  getItemFromCart: (_id: string) => any | undefined;
  isInCart: (_id: string) => boolean;
  clearCart: () => void;
}

export const cartContext = React.createContext<CartProviderState | undefined>(
  undefined
);

cartContext.displayName = 'CartContext';

export const useCart = () => {
  const context = React.useContext(cartContext);
  if (context === undefined) {
    throw new Error(`useCart must be used within a CartProvider`);
  }
  return context;
};

export function CartProvider(props: React.PropsWithChildren<any>) {
  const [state, setState] = React.useState<State>({
    items: [],
    totalItems: 0,
    totalUniqueItems: 0,
    isEmpty: true,
  });

  const addItemToCart = React.useCallback((item: CartItem) => {
    setState((prevState) => {
      const existingItem = prevState.items.find((i) => i.id === item.id);
      if (existingItem) {
        const updatedItems = prevState.items.map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
        );
        return {
          ...prevState,
          items: updatedItems,
          totalItems: prevState.totalItems + 1,
        };
      }
      return {
        ...prevState,
        items: [...prevState.items, { ...item, quantity: 1 }],
        totalItems: prevState.totalItems + 1,
        totalUniqueItems: prevState.totalUniqueItems + 1,
        isEmpty: false,
      };
    });
  }, []);

  const removeItemFromCart = React.useCallback((id: string) => {
    setState((prevState) => {
      const existingItem = prevState.items.find((i) => i.id === id);
      if (existingItem && existingItem.quantity > 1) {
        const updatedItems = prevState.items.map((i) =>
          i.id === id ? { ...i, quantity: i.quantity - 1 } : i
        );
        return {
          ...prevState,
          items: updatedItems,
          totalItems: prevState.totalItems - 1,
        };
      }
      const filteredItems = prevState.items.filter((i) => i.id !== id);
      return {
        ...prevState,
        items: filteredItems,
        totalItems: prevState.totalItems - 1,
        totalUniqueItems: prevState.totalUniqueItems - 1,
        isEmpty: filteredItems.length === 0,
      };
    });
  }, []);

  const clearItemFromCart = React.useCallback((id: string) => {
    setState((prevState) => {
      const existingItem = prevState.items.find((i) => i.id === id);
      if (!existingItem) return prevState;

      const filteredItems = prevState.items.filter((i) => i.id !== id);
      return {
        ...prevState,
        items: filteredItems,
        totalItems: prevState.totalItems - existingItem.quantity,
        totalUniqueItems: prevState.totalUniqueItems - 1,
        isEmpty: filteredItems.length === 0,
      };
    });
  }, []);

  const getItemFromCart = React.useCallback(
    (id: string) => state.items.find((i) => i.id === id),
    [state.items]
  );

  const isInCart = React.useCallback(
    (id: string) => !!state.items.find((i) => i.id === id),
    [state.items]
  );

  const clearCart = React.useCallback(() => {
    setState({
      items: [],
      totalItems: 0,
      totalUniqueItems: 0,
      isEmpty: true,
    });
  }, []);

  const value = React.useMemo(
    () => ({
      ...state,
      addItemToCart,
      removeItemFromCart,
      clearItemFromCart,
      getItemFromCart,
      isInCart,
      clearCart,
    }),
    [state, addItemToCart, removeItemFromCart, clearItemFromCart, getItemFromCart, isInCart, clearCart]
  );

  return <cartContext.Provider value={value}>{props.children}</cartContext.Provider>;
}
